import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Gauge, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function QuoteSlaSettingsSection() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get("/quote-sla/settings")
      .then(({ data }) => setForm({
        amber_days: data.amber_days,
        red_days: data.red_days,
        alert_email: data.alert_email || "",
        red_cc_emails: (data.red_cc_emails || []).join(", "),
      }))
      .catch(() => toast.error("Failed to load quote SLA settings"));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/quote-sla/settings", form);
      setForm({
        amber_days: data.amber_days, red_days: data.red_days,
        alert_email: data.alert_email || "",
        red_cc_emails: (data.red_cc_emails || []).join(", "),
      });
      toast.success("Quote SLA settings saved — applies to all new checks");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  if (!form) return <div className="flex items-center justify-center h-32"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="quote-sla-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold font-[Chivo] flex items-center gap-2"><Gauge size={18} /> Quote SLA Rules</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Thresholds are in working days (Mon–Fri). Changes apply to the next SLA check — already-triggered alerts are unaffected.</p>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="quote-sla-save-btn">
          <FloppyDisk size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="border border-amber-300 dark:border-amber-800 rounded-md p-4 bg-amber-50/60 dark:bg-amber-950/20">
          <Label className="text-amber-800 dark:text-amber-300 font-semibold">Amber SLA — no quote added</Label>
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" min={1} value={form.amber_days}
              onChange={e => setForm({ ...form, amber_days: e.target.value })}
              className="w-24 font-mono" data-testid="quote-sla-amber-days" />
            <span className="text-xs text-muted-foreground">working days</span>
          </div>
        </div>
        <div className="border border-red-300 dark:border-red-800 rounded-md p-4 bg-red-50/60 dark:bg-red-950/20">
          <Label className="text-red-800 dark:text-red-300 font-semibold">Red SLA — quote not sent to client</Label>
          <div className="flex items-center gap-2 mt-2">
            <Input type="number" min={2} value={form.red_days}
              onChange={e => setForm({ ...form, red_days: e.target.value })}
              className="w-24 font-mono" data-testid="quote-sla-red-days" />
            <span className="text-xs text-muted-foreground">working days</span>
          </div>
        </div>
      </div>

      <div className="border border-border rounded-md p-4 space-y-4">
        <div>
          <Label>Alert email (amber + red)</Label>
          <Input type="email" value={form.alert_email}
            onChange={e => setForm({ ...form, alert_email: e.target.value })}
            placeholder="hello@yourcompany.com" className="mt-1" data-testid="quote-sla-alert-email" />
          <p className="text-[11px] text-muted-foreground mt-1">Where breach alerts are sent. Leave blank to send to your admin users instead.</p>
        </div>
        <div>
          <Label>Red alert CC emails</Label>
          <Input value={form.red_cc_emails}
            onChange={e => setForm({ ...form, red_cc_emails: e.target.value })}
            placeholder="pete@yourcompany.com, lynsey@yourcompany.com" className="mt-1" data-testid="quote-sla-cc-emails" />
          <p className="text-[11px] text-muted-foreground mt-1">Comma-separated. CC'd only on red (missed 2nd threshold) breaches.</p>
        </div>
      </div>
    </div>
  );
}
